import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { CheckboxModule } from 'primeng/checkbox';

import { DropdownItem } from './member.model';
import { DigitsOnlyDirective } from '../../shared/digits-only';

/**
 * Step 2 of the member-onboarding wizard — Nominee Details.
 *
 * The parent owns the nominees FormArray (and builds each group + validators);
 * this component only renders the dynamic list, displays errors and emits
 * add/remove events. It also shows a live "percentages must total 100" hint.
 */
@Component({
  selector: 'app-nominee-details-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectModule,
    DatePickerModule,
    InputTextModule,
    ButtonModule,
    MessageModule,
    CheckboxModule,
    DigitsOnlyDirective,
  ],
  templateUrl: './nominee-details-step.html',
  styleUrl: './nominee-details-step.css',
})
export class NomineeDetailsStep {
  /** The parent's nominees FormArray — groups are created by the parent. */
  @Input({ required: true }) nominees!: FormArray;

  /** Relationship dropdown options (editable so a new value can be typed). */
  @Input() relationshipOptions: DropdownItem[] = [];

  /** Ask the parent to push a new nominee group. */
  @Output() addNominee = new EventEmitter<void>();

  /** Ask the parent to remove the nominee at the given index. */
  @Output() removeNominee = new EventEmitter<number>();

  /** A nominee's "same as member" checkbox changed — parent mirrors/unlocks its contact fields. */
  @Output() sameAsMemberToggle = new EventEmitter<number>();

  /** Max selectable DOB — no nominee can be born in the future. */
  readonly today = new Date();

  /** Cast a FormArray control to FormGroup for template binding. */
  asGroup(ctrl: AbstractControl): FormGroup {
    return ctrl as FormGroup;
  }

  /** Live sum of every nominee's nomination percentage. */
  totalPercentage(): number {
    return this.nominees.controls.reduce(
      (sum, group) => sum + (Number(group.get('nominationPercentage')?.value) || 0),
      0,
    );
  }
}
